import { useSyncExternalStore } from 'react'

/**
 * A lazily imported module held outside React, so a component can render it in the commit
 * that asks for it once it has arrived (React.lazy would suspend and hold the reveal back
 * even when the chunk is already cached). `use()` is null until the import resolves.
 */
export function lazyModule<T>(importer: () => Promise<T>) {
  let mod: T | null = null
  let loading: Promise<void> | null = null
  const subscribers = new Set<() => void>()
  const load = (): Promise<void> => {
    loading ??= importer().then(
      (m) => {
        mod = m
        for (const f of subscribers) f()
      },
      () => {
        loading = null // offline / evicted chunk: the next ask tries again
      },
    )
    return loading
  }
  const use = (): T | null =>
    useSyncExternalStore(
      (cb) => {
        subscribers.add(cb)
        return () => subscribers.delete(cb)
      },
      () => mod,
      () => null,
    )
  return { load, use }
}

/**
 * Runs `fn` once the page has settled: the first complete forecast (or the load event, in
 * demo/offline builds that never mark one), then `delayMs` and a quiet main thread. Returns cleanup.
 */
export function whenSettled(fn: () => void, delayMs = 1500): () => void {
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void }
  let timer = 0
  let idle = 0
  const settle = () => {
    if (timer) return
    timer = window.setTimeout(() => {
      if (w.requestIdleCallback) idle = w.requestIdleCallback(fn, { timeout: 3000 })
      else fn()
    }, delayMs)
  }
  window.addEventListener('wf:report-full', settle, { once: true })
  if (document.readyState === 'complete') settle()
  else window.addEventListener('load', settle, { once: true })
  return () => {
    window.removeEventListener('wf:report-full', settle)
    window.removeEventListener('load', settle)
    window.clearTimeout(timer)
    if (idle && w.cancelIdleCallback) w.cancelIdleCallback(idle)
  }
}
